import { Button, Modal, ModalBody, ModalCloseButton, ModalContent, ModalHeader, ModalOverlay, Stack, Text, useToast } from "@chakra-ui/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { createCertificate, uploadFile } from "../../../../api";
import { colors } from "../../../../styles";
import FileAttach from "../../../Auth/SignUp/FileAttach";
import { getSignUpErrorMessage } from "../../../Auth/SignUp/errors";
import { FieldMessage } from "../../../Auth/SignUp/StepLayout";

interface CreateCertificateModalProps {
    isOpen: boolean;
    onClose: () => void;
}

export default function CreateCertificateModal({ isOpen, onClose }: CreateCertificateModalProps) {
    const queryClient = useQueryClient();
    const toast = useToast();
    const [certificateImage, setCertificateImage] = useState<File | null>(null);
    const [errorMessage, setErrorMessage] = useState("");

    useEffect(() => {
        if (isOpen) {
            setCertificateImage(null);
            setErrorMessage("");
        }
    }, [isOpen]);

    const uploadFileMutation = useMutation({ mutationFn: uploadFile });
    const createCertificateMutation = useMutation({
        mutationFn: createCertificate,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["me"] });
            toast({
                title: "증명서가 등록되었습니다.",
                status: "success",
                position: "top",
                duration: 2000,
                isClosable: true,
            });
            onClose();
        },
    });

    const onSave = async () => {
        if (!certificateImage) {
            setErrorMessage("증명서 사진을 첨부해주세요.");
            return;
        }

        setErrorMessage("");

        try {
            const image = await uploadFileMutation.mutateAsync({ file: certificateImage });
            await createCertificateMutation.mutateAsync({ image });
        } catch (error) {
            setErrorMessage(getSignUpErrorMessage(error, "재학 증명서 등록 중 문제가 발생했습니다."));
        }
    };

    return (
        <Modal motionPreset="slideInBottom" isOpen={isOpen} onClose={onClose} size="xl">
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>
                    <Text fontSize={20} fontWeight={700} color={colors.black}>증명서 등록</Text>
                    <ModalCloseButton />
                </ModalHeader>
                <ModalBody padding={10}>
                    <Text fontSize={12} fontWeight={500} color={colors.g3} marginBottom={5}>
                        신청일 기준 6개월 이내 발급된 증명서만 가능합니다.
                    </Text>
                    <FileAttach
                        label="01 | 재학증명서 또는 졸업증명서"
                        inputId="create-certificate-upload"
                        file={certificateImage}
                        onChange={(file) => {
                            setCertificateImage(file);
                            setErrorMessage("");
                        }}
                    />
                    <FieldMessage message={errorMessage} />
                    <Stack marginTop={8} width="100%">
                        <Button
                            onClick={onSave}
                            isLoading={uploadFileMutation.isPending || createCertificateMutation.isPending}
                            isDisabled={!certificateImage}
                            width="100%"
                            height="52px"
                            backgroundColor={colors.blue}
                            color={colors.white}
                            borderRadius={10}
                        >
                            저장
                        </Button>
                    </Stack>
                </ModalBody>
            </ModalContent>
        </Modal>
    );
}
